import CloseIcon from '@mui/icons-material/Close';
import LiveHelpOutlinedIcon from '@mui/icons-material/LiveHelpOutlined';
import { Dialog, IconButton, Stack, Typography } from '@mui/material';
import { ProductGroups } from 'enums';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Shadows } from 'theme';
import { makeStyles } from 'tss-react/mui';
import BlacklistingContacts from './blacklisting-contacts';
import CampaignBriefing from './campaign-briefing';
import CampaignFlow from './campaign-flow';
import CampaignOverview from './campaign-overview';
import CampaignSettings from './campaign-settings';
import QualifyingContacts from './qualifying-contacts';
import SourcingContacts from './sourcing-contacts';
import SynchronizeAccount from './synchronize-account';
import { FaqItemKey } from './faq-dialog.types';

const ActionItem: React.FC<{ activeFaq: FaqItemKey; actionKey: FaqItemKey; title: string; setActiveFaq(faq: FaqItemKey): () => void }> = ({
  activeFaq,
  actionKey,
  title,
  setActiveFaq,
}) => {
  const { classes, cx } = useStyles();

  const isActive = activeFaq === actionKey;

  return (
    <Stack className={classes.actionItem} direction={'row'} alignItems={'center'} gap={2} onClick={setActiveFaq(actionKey)}>
      <Typography className={cx(classes.actionItemTitle, isActive && 'active')}>{title}</Typography>
    </Stack>
  );
};

interface Props {
  open: boolean;
  activeFaq: FaqItemKey;
  productGroup: ProductGroups;

  setActiveFaq(faq: FaqItemKey): () => void;

  handleClose: () => void;
}

const FaqDialog: React.FC<Props> = ({ open, activeFaq, productGroup, setActiveFaq, handleClose }) => {
  const { classes } = useStyles();
  const { t } = useTranslation();

  const renderStepContent = () => {
    switch (activeFaq) {
      case FaqItemKey.SYNCHRONIZING_YOUR_ACCOUNT:
        return <SynchronizeAccount />;
      case FaqItemKey.BRIEFING:
        return <CampaignBriefing />;
      case FaqItemKey.SOURCING_CONTACTS:
        return <SourcingContacts productGroup={productGroup} />;
      case FaqItemKey.QUALIFYING_CONTACTS:
        return <QualifyingContacts productGroup={productGroup} />;
      case FaqItemKey.BLACKLISTING_CONTACTS:
        return <BlacklistingContacts />;
      case FaqItemKey.CREATING_CAMPAIGN_FLOW:
        return <CampaignFlow />;
      case FaqItemKey.CAMPAIGN_SETTINGS:
        return <CampaignSettings />;
      case FaqItemKey.CAMPAIGN_OVERVIEW_PERFORMANCE:
        return <CampaignOverview />;
      default:
        return <></>;
    }
  };

  return (
    <Dialog open={open} classes={{ paper: classes.paper }} fullWidth={true} maxWidth={'xl'} transitionDuration={0}>
      <IconButton className={classes.closeIcon} onClick={handleClose}>
        <CloseIcon />
      </IconButton>
      <Stack className={classes.content} direction={'row'} justifyContent={'center'}>
        <div className={classes.sidebar}>
          <Stack className={classes.titleContainer} direction={'row'} alignItems={'center'} gap={3}>
            <LiveHelpOutlinedIcon className={classes.icon} />
            <div>
              <Typography className={classes.title}>{t('frequentlyAskedQuestions')}</Typography>
            </div>
          </Stack>
          <Stack className={classes.stepContainer} direction={'column'}>
            <ActionItem
              activeFaq={activeFaq}
              actionKey={FaqItemKey.SYNCHRONIZING_YOUR_ACCOUNT}
              title={t('synchronizingYourAccount')}
              setActiveFaq={setActiveFaq}
            />
            <ActionItem activeFaq={activeFaq} actionKey={FaqItemKey.BRIEFING} title={t('campaignBriefing')} setActiveFaq={setActiveFaq} />
            <ActionItem activeFaq={activeFaq} actionKey={FaqItemKey.SOURCING_CONTACTS} title={t('sourcingContacts')} setActiveFaq={setActiveFaq} />
            <ActionItem
              activeFaq={activeFaq}
              actionKey={FaqItemKey.QUALIFYING_CONTACTS}
              title={t('qualifyingContacts')}
              setActiveFaq={setActiveFaq}
            />
            <ActionItem
              activeFaq={activeFaq}
              actionKey={FaqItemKey.BLACKLISTING_CONTACTS}
              title={t('blacklistingContacts')}
              setActiveFaq={setActiveFaq}
            />
            <ActionItem activeFaq={activeFaq} actionKey={FaqItemKey.CREATING_CAMPAIGN_FLOW} title={t('campaignFlow')} setActiveFaq={setActiveFaq} />
            <ActionItem activeFaq={activeFaq} actionKey={FaqItemKey.CAMPAIGN_SETTINGS} title={t('campaignSettings')} setActiveFaq={setActiveFaq} />
            <ActionItem
              activeFaq={activeFaq}
              actionKey={FaqItemKey.CAMPAIGN_OVERVIEW_PERFORMANCE}
              title={t('campaignOverviewPerformance')}
              setActiveFaq={setActiveFaq}
            />
          </Stack>
        </div>
        <div className={classes.body}>{renderStepContent()}</div>
      </Stack>
    </Dialog>
  );
};

const useStyles = makeStyles()((theme) => ({
  paper: {
    borderRadius: '12px',
    height: '100%',
    [theme.breakpoints.up('xl')]: {
      maxHeight: 'calc(950px - 64px)',
    },
  },
  closeIcon: {
    position: 'absolute',
    top: '16px',
    right: '16px',
    cursor: 'pointer',
    zIndex: 1,
  },
  content: {
    height: '100%',
    width: '100%',
    boxShadow: Shadows.GREY_SHADOW_LIGHT,
  },
  sidebar: {
    height: '100%',
    width: '340px',
    display: 'flex',
    flexDirection: 'column',
    borderRight: `1px solid ${theme.palette.divider}`,
  },
  titleContainer: {
    padding: '16px',
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
  icon: {
    fontSize: theme.typography.pxToRem(40),
    color: theme.palette.primary.main,
  },
  title: {
    fontSize: theme.typography.pxToRem(20),
    fontWeight: 'bold',
    color: theme.palette.text.primary,
  },
  stepContainer: {
    padding: '16px 32px',
    overflow: 'auto',
  },
  actionItem: {
    padding: '12px',
    borderRadius: '8px',
    cursor: 'pointer',
    '&:hover': {
      backgroundColor: theme.palette.action.hover,
    },
  },
  actionItemTitle: {
    fontSize: theme.typography.pxToRem(16),
    color: theme.palette.text.secondary,
    fontWeight: 'normal',
    '&.active': {
      color: theme.palette.primary.main,
      fontWeight: 'bold',
    },
  },
  body: {
    height: '100%',
    width: 'calc(100% - 340px)',
    background: '#FFF',
    display: 'flex',
    flexDirection: 'column',
  },
}));

export default FaqDialog;
